/**
 * The apothecary's room shell (lifted from lantern-water's `world.js`
 * `createShop`): the floor slab and its framed planks, the back and side
 * walls with their posts, braces and mouldings, the fitted joinery
 * (`shop-joinery.ts`), the ceiling beam, the two arched windows and the two
 * authored window shafts (soft additive cards, not volumetric light). Origin:
 * the room's centre on the floor. No params.
 *
 * The shafts are named `shop-window-shaft` (`userData.shaft`); their shared
 * material's `strength` uniform is the view's to drive (the prototype set it
 * to 1 − night, 0 on Lightweight). The window lights are the scene's.
 */
import * as THREE from 'three';
import { shopJoinery } from '../shop-joinery.ts';
import type { Prefab } from './types.ts';

export const prefab: Prefab = {
  id: 'shop_room',
  buildPrefab(ctx) {
    const root = new THREE.Group();
    const { m } = ctx;
    ctx.box(root, 0, -0.21, 0, 13.5, 0.42, 10.7, m.darkWood);
    ctx.box(root, 0, 0.016, 0, 13.2, 0.06, 10.4, m.wood);
    for (let x = -6.3; x < 6.5; x += 0.55) ctx.box(root, x, 0.054, 0, 0.018, 0.012, 10.4, m.darkWood);
    ctx.box(root, 0, 2.4, -5.25, 13.6, 4.8, 0.28, m.plaster);
    ctx.box(root, -6.65, 2.4, 0, 0.3, 4.8, 10.5, m.plaster);
    ctx.box(root, 6.65, 1.2, -2.5, 0.3, 2.4, 5, m.plaster);
    for (const y of [0.25, 1.15, 4.6]) {
      ctx.box(root, 0, y, -5.02, 13.5, 0.18, 0.2, m.darkWood);
      ctx.box(root, -6.47, y, 0, 0.18, 0.18, 10.5, m.darkWood);
    }
    for (const x of [-6.4, -3.3, 0, 3.3, 6.4]) {
      ctx.box(root, x, 2.35, -5.03, 0.16, 4.7, 0.22, m.darkWood);
      ctx.line(root, [x, 3.7, -4.88], [x + 0.7, 4.53, -4.88], 0.12, m.darkWood);
    }
    for (const z of [-4.8, -1.4, 2, 5]) ctx.box(root, -6.42, 2.4, z, 0.19, 4.8, 0.17, m.darkWood);
    shopJoinery(ctx.kit, root);
    // The ceiling beam stops short of the camera-facing cutaway.
    ctx.box(root, 0, 4.67, -4.6, 13.4, 0.2, 0.21, m.darkWood);
    ctx.windowFrame(root, -2.6, 2.55, -5, 1.32, 2, false);
    ctx.windowFrame(root, 2.55, 2.55, -5, 1.15, 2, false);
    const shaft = ctx.kit.shared(
      'shop-room:shaft',
      () =>
        new THREE.ShaderMaterial({
          uniforms: { strength: { value: 1 } },
          transparent: true,
          depthWrite: false,
          side: THREE.DoubleSide,
          blending: THREE.AdditiveBlending,
          vertexShader: 'varying vec2 vUv;void main(){vUv=uv;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.);}',
          fragmentShader:
            'uniform float strength;varying vec2 vUv;void main(){float edge=smoothstep(0.,.28,vUv.x)*(1.-smoothstep(.72,1.,vUv.x));float alpha=edge*(.02+.045*vUv.y)*smoothstep(0.,.15,vUv.y);gl_FragColor=vec4(1.,.76,.4,alpha*strength);}',
        }),
    );
    for (const wx of [-2.6, 2.55]) {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute([wx - 0.5, 3.7, -4.87, wx + 0.5, 3.7, -4.87, wx + 2.3, 0.09, 2.3, wx - 1, 0.09, 2.3], 3));
      geo.setAttribute('uv', new THREE.Float32BufferAttribute([0, 1, 1, 1, 1, 0, 0, 0], 2));
      geo.setIndex([0, 1, 2, 0, 2, 3]);
      geo.computeVertexNormals();
      const card = ctx.mesh(root, geo, shaft, 0, 0, 0);
      card.castShadow = false;
      card.name = 'shop-window-shaft';
      card.userData['shaft'] = true;
    }
    return { root };
  },
};
